import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Textarea } from "../components/Textarea";
import { Field } from "../components/Field";
import { BaitPicker, BaitSelection } from "../components/BaitPicker";
import { RigPicker, RigSelection } from "../components/RigPicker";

export default function LakeDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    data: lake,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["carpintel_lake", id],
    queryFn: () =>
      fetch(`/app-api/lakes/${id}`).then((r) => {
        if (!r.ok) throw new Error("Lake not found");
        return r.json();
      }),
  });
  const [form, setForm] = useState({
    name: "",
    location: "",
    latitude: "",
    longitude: "",
    notes: "",
  });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (lake)
      setForm({
        name: lake.name || "",
        location: lake.location || "",
        latitude: lake.latitude ?? "",
        longitude: lake.longitude ?? "",
        notes: lake.notes || "",
      });
  }, [lake]);

  async function saveLake(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    const response = await fetch(`/app-api/lakes/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...form,
        latitude: form.latitude || null,
        longitude: form.longitude || null,
      }),
    });
    const result = await response.json();
    setSaving(false);
    if (!response.ok) {
      setMessage(result.error || "Could not save changes");
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["carpintel_lakes"] });
    await queryClient.invalidateQueries({ queryKey: ["carpintel_lake", id] });
    setEditing(false);
    setMessage("Changes saved");
  }

  if (isLoading) return <PageMessage text="Loading lake…" />;
  if (error || !lake)
    return <PageMessage text="That lake could not be found." link="/lakes" />;
  return (
    <div className="mx-auto max-w-[760px] space-y-6 px-4 py-6 pb-28 md:py-8 md:pb-10">
      <header>
        <Link
          to="/lakes"
          className="text-sm font-medium text-accent hover:underline"
        >
          My lakes
        </Link>
        <div className="mt-2 flex min-w-0 items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-semibold tracking-tight text-primary">
              {lake.name}
            </h1>
            <p className="mt-1 text-sm text-secondary">
              {lake.location || "Location not recorded"}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button
              variant="secondary"
              onClick={() => setEditing((value) => !value)}
            >
              {editing ? "Close edit" : "Edit lake"}
            </Button>
          </div>
        </div>
      </header>
      {editing ? (
        <form
          onSubmit={saveLake}
          className="space-y-4 rounded-lg border border-border bg-raised p-4"
        >
          <Field label="Lake name" id="detail-lake-name">
            <Input
              id="detail-lake-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </Field>
          <Field label="Location" id="detail-lake-location">
            <Input
              id="detail-lake-location"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
            />
          </Field>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field
              label="Latitude"
              id="detail-lake-latitude"
              help="Optional coordinates for future automatic weather lookup."
            >
              <Input
                id="detail-lake-latitude"
                type="number"
                step="0.000001"
                value={form.latitude}
                onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                placeholder="51.7520"
              />
            </Field>
            <Field
              label="Longitude"
              id="detail-lake-longitude"
              help="Optional coordinates for future automatic weather lookup."
            >
              <Input
                id="detail-lake-longitude"
                type="number"
                step="0.000001"
                value={form.longitude}
                onChange={(e) =>
                  setForm({ ...form, longitude: e.target.value })
                }
                placeholder="-1.2577"
              />
            </Field>
          </div>
          <Field label="Notes" id="detail-lake-notes">
            <Textarea
              id="detail-lake-notes"
              rows={5}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </Field>
          {message ? (
            <p className="text-sm text-secondary" role="status">
              {message}
            </p>
          ) : null}
          <div className="flex gap-3">
            <Button type="submit" isLoading={saving}>
              Save changes
            </Button>
            <Button
              type="button"
              variant="tertiary"
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <>
          <section className="rounded-lg border border-border bg-raised p-4">
            <p className="text-sm leading-6 text-secondary">
              {lake.notes ||
                "No notes yet. Add peg features, access details or useful bait observations."}
            </p>
          </section>
          <div className="grid grid-cols-2 gap-3">
            <Metric label="Sessions" value={lake.session_count} />
            <Metric label="Catches" value={lake.catch_count} />
          </div>
          <Button asChild variant="tertiary">
            <Link to={`/sessions/new?lake_id=${id}`}>Start a session here</Link>
          </Button>
          <Button asChild variant="secondary">
            <Link to={`/lakes/${id}/map`}>Interactive lake map</Link>
          </Button>

          {/* Fix 5: Remove from My Lakes */}
          {!confirmingRemove ? (
            <button
              type="button"
              onClick={() => setConfirmingRemove(true)}
              className="text-sm text-error hover:underline text-left"
            >
              Remove from My Lakes
            </button>
          ) : (
            <div className="rounded-lg border border-error bg-error-weak p-4 space-y-3">
              <p className="text-sm font-semibold text-error">
                Remove {lake.name} from My Lakes?
              </p>
              <p className="text-sm text-secondary">
                This removes only your My Lakes entry. Historical sessions,
                catches and the lake catalogue are not affected. You can add it
                again later.
              </p>
              {message && confirmingRemove ? (
                <p className="text-sm text-error" role="alert">
                  {message}
                </p>
              ) : null}
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  isLoading={removing}
                  onClick={async () => {
                    setRemoving(true);
                    setMessage("");
                    try {
                      const res = await fetch(`/app-api/lakes/${id}`, {
                        method: "DELETE",
                      });
                      if (!res.ok) {
                        const d = await res.json().catch(() => ({}));
                        setMessage(
                          d.error || "Could not remove lake. Try again.",
                        );
                        return;
                      }
                      await queryClient.invalidateQueries({
                        queryKey: ["carpintel_lakes"],
                      });
                      navigate("/lakes");
                    } catch {
                      setMessage("Could not remove lake. Try again.");
                    } finally {
                      setRemoving(false);
                    }
                  }}
                >
                  Remove from My Lakes
                </Button>
                <Button
                  variant="tertiary"
                  onClick={() => {
                    setConfirmingRemove(false);
                    setMessage("");
                  }}
                >
                  Keep it
                </Button>
              </div>
            </div>
          )}

          <SavedSpotsSection lakeId={id!} />
        </>
      )}
    </div>
  );
}

// ── Spot form types and helpers ──────────────────────────────────────────
const emptySpotBait = (): BaitSelection => ({
  canonical_id: null,
  user_bait_id: null,
  snapshot: "",
});
const emptySpotRig = (): RigSelection => ({
  canonical_id: null,
  user_rig_id: null,
  snapshot: "",
});

type SpotFormState = {
  name: string;
  distance_m: string;
  depth_ft: string;
  substrate: string;
  feature_description: string;
  baiting_approach: string;
  deeper_reference: string;
  private_notes: string;
  rig_selection: RigSelection;
  feed_bait_selection: BaitSelection;
  hookbait_selection: BaitSelection;
};

const emptySpotForm = (): SpotFormState => ({
  name: "",
  distance_m: "",
  depth_ft: "",
  substrate: "",
  feature_description: "",
  baiting_approach: "",
  deeper_reference: "",
  private_notes: "",
  rig_selection: emptySpotRig(),
  feed_bait_selection: emptySpotBait(),
  hookbait_selection: emptySpotBait(),
});

function spotPayload(f: SpotFormState) {
  return {
    name: f.name,
    distance_m: f.distance_m || null,
    depth_ft: f.depth_ft || null,
    substrate: f.substrate || null,
    feature_description: f.feature_description || null,
    baiting_approach: f.baiting_approach || null,
    deeper_reference: f.deeper_reference || null,
    private_notes: f.private_notes || null,
    default_rig_id: f.rig_selection.canonical_id || null,
    default_user_rig_id: f.rig_selection.user_rig_id || null,
    default_feed_bait_id: f.feed_bait_selection.canonical_id || null,
    default_feed_user_bait_id: f.feed_bait_selection.user_bait_id || null,
    default_hookbait_id: f.hookbait_selection.canonical_id || null,
    default_hookbait_user_bait_id: f.hookbait_selection.user_bait_id || null,
  };
}

function SpotFormPanel({
  form,
  setForm,
  saving,
  msg,
  onSubmit,
  onCancel,
  submitLabel,
}: {
  form: SpotFormState;
  setForm: React.Dispatch<React.SetStateAction<SpotFormState>>;
  saving: boolean;
  msg: string;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  submitLabel: string;
}) {
  const sf = (key: keyof SpotFormState, val: string) =>
    setForm((f) => ({ ...f, [key]: val }));
  return (
    <form
      onSubmit={onSubmit}
      className="space-y-3 rounded-lg border border-border bg-raised p-4"
    >
      <Field label="Spot name *" id="sp-name">
        <Input
          id="sp-name"
          value={form.name}
          onChange={(e) => sf("name", e.target.value)}
          placeholder="e.g. Far margin point"
          required
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Distance (m)" id="sp-dist">
          <Input
            id="sp-dist"
            type="number"
            step="0.5"
            min="0"
            value={form.distance_m}
            onChange={(e) => sf("distance_m", e.target.value)}
            placeholder="65"
          />
        </Field>
        <Field label="Depth (ft)" id="sp-depth">
          <Input
            id="sp-depth"
            type="number"
            step="0.1"
            min="0"
            value={form.depth_ft}
            onChange={(e) => sf("depth_ft", e.target.value)}
            placeholder="9"
          />
        </Field>
      </div>
      <Field label="Substrate" id="sp-sub">
        <Input
          id="sp-sub"
          value={form.substrate}
          onChange={(e) => sf("substrate", e.target.value)}
          placeholder="e.g. gravel, silt, clay"
        />
      </Field>
      <Field label="Feature description" id="sp-feat">
        <Input
          id="sp-feat"
          value={form.feature_description}
          onChange={(e) => sf("feature_description", e.target.value)}
          placeholder="e.g. gravel bar at 70m, near island"
        />
      </Field>
      <RigPicker
        id="sp-rig"
        label="Default rig"
        value={form.rig_selection}
        onChange={(sel) => setForm((f) => ({ ...f, rig_selection: sel }))}
        placeholder="Search rigs…"
      />
      <BaitPicker
        id="sp-feed"
        label="Default feed bait"
        value={form.feed_bait_selection}
        onChange={(sel) => setForm((f) => ({ ...f, feed_bait_selection: sel }))}
        presentationRole="feed"
        placeholder="Search baits…"
      />
      <BaitPicker
        id="sp-hook"
        label="Default hookbait"
        value={form.hookbait_selection}
        onChange={(sel) => setForm((f) => ({ ...f, hookbait_selection: sel }))}
        presentationRole="hookbait"
        placeholder="Search hookbaits…"
      />
      <Field label="Baiting approach" id="sp-bait-app">
        <Input
          id="sp-bait-app"
          value={form.baiting_approach}
          onChange={(e) => sf("baiting_approach", e.target.value)}
          placeholder="e.g. light scatter, spod, stick mix"
        />
      </Field>
      <Field
        label="Deeper reference"
        id="sp-deeper"
        help="Private sonar reference — never shared."
      >
        <Input
          id="sp-deeper"
          value={form.deeper_reference}
          onChange={(e) => sf("deeper_reference", e.target.value)}
          placeholder="Optional scan label"
        />
      </Field>
      <Field
        label="Private notes"
        id="sp-notes"
        help="Never shared or visible to others."
      >
        <Textarea
          id="sp-notes"
          rows={2}
          value={form.private_notes}
          onChange={(e) => sf("private_notes", e.target.value)}
          placeholder="Anything to remember about this spot…"
        />
      </Field>
      {msg && <p className="text-sm text-error">{msg}</p>}
      <div className="flex gap-3">
        <Button type="submit" isLoading={saving}>
          {submitLabel}
        </Button>
        <Button type="button" variant="tertiary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function SavedSpotsSection({ lakeId }: { lakeId: string }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [addForm, setAddForm] = useState<SpotFormState>(emptySpotForm);
  const [editForm, setEditForm] = useState<SpotFormState>(emptySpotForm);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const { data: spots = [], isLoading } = useQuery({
    queryKey: ["carpintel_saved_spots", lakeId],
    queryFn: () =>
      fetch(`/app-api/lakes/${lakeId}/spots`).then((r) => r.json()),
  });

  async function addSpot(e: React.FormEvent) {
    e.preventDefault();
    if (!addForm.name.trim()) return;
    setSaving(true);
    setMsg("");
    try {
      const res = await fetch(`/app-api/lakes/${lakeId}/spots`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(spotPayload(addForm)),
      });
      if (!res.ok) throw new Error("Could not save spot");
      await queryClient.invalidateQueries({
        queryKey: ["carpintel_saved_spots", lakeId],
      });
      setAdding(false);
      setAddForm(emptySpotForm());
    } catch {
      setMsg("Could not save spot. Try again.");
    }
    setSaving(false);
  }

  function startEdit(s: any) {
    setEditingId(s.id);
    setEditForm({
      name: s.name || "",
      distance_m: s.distance_m != null ? String(s.distance_m) : "",
      depth_ft: s.depth_ft != null ? String(s.depth_ft) : "",
      substrate: s.substrate || "",
      feature_description: s.feature_description || "",
      baiting_approach: s.baiting_approach || "",
      deeper_reference: s.deeper_reference || "",
      private_notes: s.private_notes || "",
      rig_selection: s.default_rig_snapshot
        ? {
            canonical_id: s.default_rig_id ?? null,
            user_rig_id: s.default_user_rig_id ?? null,
            snapshot: s.default_rig_snapshot,
          }
        : emptySpotRig(),
      feed_bait_selection: s.default_feed_bait_snapshot
        ? {
            canonical_id: s.default_feed_bait_id ?? null,
            user_bait_id: s.default_feed_user_bait_id ?? null,
            snapshot: s.default_feed_bait_snapshot,
          }
        : emptySpotBait(),
      hookbait_selection: s.default_hookbait_snapshot
        ? {
            canonical_id: s.default_hookbait_id ?? null,
            user_bait_id: s.default_hookbait_user_bait_id ?? null,
            snapshot: s.default_hookbait_snapshot,
          }
        : emptySpotBait(),
    });
  }

  async function saveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editForm.name.trim() || !editingId) return;
    setSaving(true);
    setMsg("");
    try {
      const res = await fetch(`/app-api/spots/${editingId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(spotPayload(editForm)),
      });
      if (!res.ok) throw new Error("Could not update spot");
      await queryClient.invalidateQueries({
        queryKey: ["carpintel_saved_spots", lakeId],
      });
      setEditingId(null);
    } catch {
      setMsg("Could not save changes. Try again.");
    }
    setSaving(false);
  }

  async function deleteSpot(spotId: number) {
    await fetch(`/app-api/spots/${spotId}`, { method: "DELETE" });
    queryClient.invalidateQueries({
      queryKey: ["carpintel_saved_spots", lakeId],
    });
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-primary">Saved Spots</h2>
        {!adding && !editingId && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setAdding(true);
              setMsg("");
            }}
          >
            + Add Spot
          </Button>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-secondary">Loading spots…</p>
      ) : spots.length === 0 && !adding ? (
        <p className="text-sm text-secondary">
          No saved spots yet. Add spots to quickly prefill distance, depth and
          tackle when logging a catch.
        </p>
      ) : (
        <div className="space-y-2">
          {spots.map((s: any) =>
            editingId === s.id ? (
              <SpotFormPanel
                key={s.id}
                form={editForm}
                setForm={setEditForm}
                saving={saving}
                msg={msg}
                onSubmit={saveEdit}
                onCancel={() => {
                  setEditingId(null);
                  setMsg("");
                }}
                submitLabel="Save changes"
              />
            ) : (
              <div
                key={s.id}
                className="rounded-lg border border-border bg-raised p-3 space-y-0.5"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-primary">{s.name}</p>
                  <div className="flex shrink-0 gap-3">
                    <button
                      type="button"
                      onClick={() => startEdit(s)}
                      className="text-xs text-accent hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteSpot(s.id)}
                      className="text-xs text-error hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <p className="text-xs text-secondary">
                  {[
                    s.distance_m != null && `${s.distance_m}m`,
                    s.depth_ft != null && `${s.depth_ft}ft`,
                    s.substrate,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                {s.feature_description && (
                  <p className="text-xs text-secondary">
                    {s.feature_description}
                  </p>
                )}
                {s.default_rig_snapshot && (
                  <p className="text-xs text-secondary">
                    Rig: {s.default_rig_snapshot}
                  </p>
                )}
                {s.default_feed_bait_snapshot && (
                  <p className="text-xs text-secondary">
                    Feed: {s.default_feed_bait_snapshot}
                  </p>
                )}
                {s.default_hookbait_snapshot && (
                  <p className="text-xs text-secondary">
                    Hookbait: {s.default_hookbait_snapshot}
                  </p>
                )}
                {s.baiting_approach && (
                  <p className="text-xs text-secondary">
                    Approach: {s.baiting_approach}
                  </p>
                )}
              </div>
            ),
          )}
        </div>
      )}

      {adding && (
        <SpotFormPanel
          form={addForm}
          setForm={setAddForm}
          saving={saving}
          msg={msg}
          onSubmit={addSpot}
          onCancel={() => {
            setAdding(false);
            setMsg("");
          }}
          submitLabel="Save spot"
        />
      )}
    </section>
  );
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-lg border border-border bg-raised p-4">
      <p className="text-xs text-secondary">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-primary">{value}</p>
    </div>
  );
}
function PageMessage({ text, link }: { text: string; link?: string }) {
  return (
    <div className="mx-auto max-w-[760px] px-4 py-8 text-center text-sm text-secondary">
      <p>{text}</p>
      {link ? (
        <Link
          to={link}
          className="mt-3 inline-block font-medium text-accent hover:underline"
        >
          Back to lakes
        </Link>
      ) : null}
    </div>
  );
}